import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Trash2, Upload, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ReviewerLockdown from "@/components/ReviewerLockdown";

// Canonical content-type list (OTA35 Option B — 15 fixed categories).
// Mirrors `_CATEGORY_ALIASES` values in `backend/util/csv_helpers.py`;
// values here are what the DB stores + what the /explore filters
// expect. Any drift between this list and the backend importer is a
// bug (the two paths must agree on canonical spelling including the
// slashes/ampersand for "Camping/Campgrounds" and "Lodges/Inns/B&Bs").
const CANONICAL_TYPES = [
  "Hotels",
  "Resorts",
  "All-Inclusive Resorts",
  "Cruises",
  "Destinations",
  "Activities",
  "Camping/Campgrounds",
  "Unique Stays",
  "Lodges/Inns/B&Bs",
  "Cabins",
  "RV Resorts",
  "Theme Parks",
  "National Parks",
  "Flights",
  "Transportation",
];

// Legacy tokens seen in older admin-authored rows (pre-Option B). Kept
// only so the dropdown surfaces the existing value with a "⚠ legacy"
// marker rather than silently swapping it out. Migration is a human
// decision, not something we do automatically on load.
const LEGACY_TYPES = new Set([
  "resort", "hotel", "destination", "ski_resort", "camping", "attraction",
]);

const BLANK = {
  type: "",  // was "hotel"; blank now so user must pick from canonical set on create
  name: "",
  city: "",
  state: "",
  country: "US",
  address: "",
  lat: 0,
  lng: 0,
  category: "",
  tags: [],
  description: "",
  featured_image: "",
  images: [],
};

const inp = "w-full px-3 py-2 rounded-lg bg-tb-cream text-tb-navy border-2 border-tb-cream-3 focus:border-tb-gold outline-none font-ui text-sm";
const inpReadOnly = "w-full px-3 py-2 rounded-lg bg-tb-cream-2 text-tb-navy/50 border-2 border-tb-cream-3 font-ui text-sm cursor-not-allowed";
const labelCls = "block font-hand text-tb-cream/80 text-sm mb-1";

export default function AdminDestinationEditPage() {
  const nav = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const isNew = id === "new" || !id;
  const [doc, setDoc] = useState(isNew ? BLANK : null);
  const [tagsInput, setTagsInput] = useState("");
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const nameRef = useRef(null);
  // "Save & keep values" — persisted per-user in localStorage so a
  // bulk-add session (e.g. 5 hotels in the same city) doesn't have
  // to re-toggle every save. Default OFF; when OFF, a create resets
  // the form + focuses Name.
  const [keepValues, setKeepValues] = useState(() => {
    try { return localStorage.getItem("admin_dest_keep_values") === "1"; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("admin_dest_keep_values", keepValues ? "1" : "0"); }
    catch { /* ignore */ }
  }, [keepValues]);

  useEffect(() => {
    if (user === null) return;
    if (user === false || (user.role !== "admin" && user.role !== "reviewer")) { nav("/auth"); }
  }, [user, nav]);

  const load = useCallback(async () => {
    if (isNew) { setDoc(BLANK); setTagsInput(""); setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/destinations/${id}`);
      setDoc(data);
      setTagsInput((data.tags || []).join(", "));
    } catch (e) {
      console.error("[AdminDestinationEdit] load failed", e);
      toast.error("Failed to load destination");
      nav("/admin/destinations");
    } finally { setLoading(false); }
  }, [id, isNew, nav]);

  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setDoc((prev) => ({ ...prev, [k]: v }));

  const buildPayload = () => {
    const p = {
      type: doc.type,
      name: (doc.name || "").trim(),
      city: (doc.city || "").trim(),
      state: (doc.state || "").trim() || null,
      country: (doc.country || "US").trim(),
      address: (doc.address || "").trim() || null,
      lat: parseFloat(doc.lat) || 0,
      lng: parseFloat(doc.lng) || 0,
      category: (doc.category || "").trim() || null,
      tags: tagsInput.split(",").map((t) => t.trim()).filter(Boolean),
      description: doc.description || "",
      featured_image: (doc.featured_image || "").trim() || null,
      images: doc.images || [],
    };
    return p;
  };

  const onSave = async () => {
    if (!doc.name?.trim() || !doc.city?.trim()) {
      toast.error("Name and city are required");
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      if (isNew) {
        const { data } = await api.post("/admin/destinations", payload);
        const savedName = data.name || payload.name;
        if (keepValues) {
          // Bulk-add mode — stay on the create page with values intact
          // so ops can tweak one field (e.g. name) and re-save. We do
          // NOT nav to the newly-created record's edit URL because
          // that would slow down the sequential-add workflow.
          toast.success(`saved "${savedName}" — values kept for next entry`);
        } else {
          // Default post-create: reset to BLANK, focus the Name input.
          // Matches the user's explicit ask: "clear the boxes for
          // adding a new one instead of leaving text there".
          setDoc({ ...BLANK });
          setTagsInput("");
          if (fileRef.current) fileRef.current.value = "";
          toast.success(`Saved "${savedName}". Form cleared — add another.`, { duration: 2000 });
          setTimeout(() => { try { nameRef.current?.focus(); } catch {} }, 50);
        }
      } else {
        const { data } = await api.put(`/admin/destinations/${id}`, payload);
        setDoc(data);
        setTagsInput((data.tags || []).join(", "));
        toast.success("saved — changes appear on /explore on next page load");
      }
    } catch (e) {
      console.error("[AdminDestinationEdit] save failed", e);
      // FastAPI 422 returns `detail` as an array of ValidationError
      // objects — those must be stringified before landing in a JSX
      // toast body or React crashes with "Objects are not valid as a
      // React child". Use the shared `formatApiErrorDetail` helper.
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const onDelete = async () => {
    if (isNew) return;
    if (!window.confirm(`Delete "${doc.name}"? This removes the card from the Explore feed on next refresh.`)) return;
    try {
      await api.delete(`/admin/destinations/${id}`);
      toast.success("destination deleted");
      nav("/admin/destinations");
    } catch (e) {
      console.error("[AdminDestinationEdit] delete failed", e);
      toast.error("Delete failed");
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (isNew) {
      toast.error("Save the destination first, then upload an image");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/admin/destinations/${id}/image`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDoc((prev) => ({ ...prev, featured_image: data.url, images: [data.url, ...(prev.images || []).filter((u) => u !== data.url)] }));
      toast.success("image uploaded");
    } catch (err) {
      console.error("[AdminDestinationEdit] upload failed", err);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (loading || !doc) {
    return <div className="min-h-screen w-full bg-tb-navy-deep text-tb-cream flex items-center justify-center font-hand text-2xl">~ loading ~</div>;
  }

  const bm = doc.booking_metadata || {};
  const bmKeys = Object.keys(bm);

  return (
    <div data-testid="admin-destination-edit-page" className={"min-h-screen w-full bg-tb-navy-deep text-tb-cream pb-16 " + (user?.role === "reviewer" ? "reviewer-locked" : "")}>
      <ReviewerLockdown />
      <header className="bg-tb-navy text-tb-gold border-b-2 border-tb-gold/70 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            data-testid="admin-destination-edit-back"
            onClick={() => nav("/admin/destinations")}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-tb-navy-deep border-2 border-tb-gold/50 font-hand text-sm"
          >
            <ArrowLeft className="w-4 h-4" /> destinations
          </button>
          <h1 className="font-serif-it text-2xl">{isNew ? "New destination" : "Edit destination"}</h1>
        </div>
        <div className="flex items-center gap-2">
          {isNew && (
            <label className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-tb-navy-deep border-2 border-tb-cream/25 font-hand text-xs text-tb-cream/85 cursor-pointer">
              <input
                type="checkbox"
                data-testid="admin-destination-keep-values"
                checked={keepValues}
                onChange={(e) => setKeepValues(e.target.checked)}
                className="accent-tb-gold"
              />
              keep values after save
            </label>
          )}
          {!isNew && (
            <button
              data-testid="admin-destination-delete-btn"
              onClick={onDelete}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-full bg-tb-navy-deep border-2 border-tb-coral text-tb-coral font-hand text-sm tb-tap"
            >
              <Trash2 className="w-4 h-4" /> delete
            </button>
          )}
          <button
            data-testid="admin-destination-save-btn"
            disabled={saving}
            onClick={onSave}
            className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-tb-gold text-tb-navy border-2 border-tb-gold font-hand text-sm tb-tap disabled:opacity-50"
          >
            <Save className="w-4 h-4" /> {saving ? "saving…" : "save"}
          </button>
        </div>
      </header>

      <div className="p-6 max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4">
        <label>
          <span className={labelCls}>Type</span>
          <select data-testid="admin-destination-field-type" className={inp} value={doc.type || ""}
            onChange={(e) => setField("type", e.target.value)}>
            {!doc.type && <option value="" disabled>{"Select a type…"}</option>}
            {doc.type && !CANONICAL_TYPES.includes(doc.type) && (
              <option value={doc.type}>{`${doc.type} — ⚠ legacy`}</option>
            )}
            {CANONICAL_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          {doc.type && !CANONICAL_TYPES.includes(doc.type) && (
            <span className="block mt-1 font-hand text-tb-gold-bright text-xs">
              {"⚠ legacy value — pick a canonical type on save (or leave to keep as-is)."}
            </span>
          )}
        </label>

        <label>
          <span className={labelCls}>Name *</span>
          <input
            ref={nameRef}
            data-testid="admin-destination-field-name"
            className={inp}
            value={doc.name || ""}
            onChange={(e) => setField("name", e.target.value)}
          />
        </label>

        <label>
          <span className={labelCls}>City *</span>
          <input data-testid="admin-destination-field-city" className={inp} value={doc.city || ""}
            onChange={(e) => setField("city", e.target.value)} />
        </label>

        <label>
          <span className={labelCls}>State (2-letter)</span>
          <input data-testid="admin-destination-field-state" className={inp} value={doc.state || ""}
            onChange={(e) => setField("state", e.target.value)} maxLength={4} />
        </label>

        <label>
          <span className={labelCls}>Country (ISO2)</span>
          <input data-testid="admin-destination-field-country" className={inp} value={doc.country || ""}
            onChange={(e) => setField("country", e.target.value)} maxLength={2} />
        </label>

        <label>
          <span className={labelCls}>Address</span>
          <input data-testid="admin-destination-field-address" className={inp} value={doc.address || ""}
            onChange={(e) => setField("address", e.target.value)} />
        </label>

        <label>
          <span className={labelCls}>Latitude</span>
          <input data-testid="admin-destination-field-lat" type="number" step="0.00001" className={inp} value={doc.lat ?? 0}
            onChange={(e) => setField("lat", e.target.value)} />
        </label>

        <label>
          <span className={labelCls}>Longitude</span>
          <input data-testid="admin-destination-field-lng" type="number" step="0.00001" className={inp} value={doc.lng ?? 0}
            onChange={(e) => setField("lng", e.target.value)} />
        </label>

        <label>
          <span className={labelCls}>Category</span>
          <input data-testid="admin-destination-field-category" className={inp} value={doc.category || ""}
            onChange={(e) => setField("category", e.target.value)} />
        </label>

        <label>
          <span className={labelCls}>Tags (comma-separated)</span>
          <input data-testid="admin-destination-field-tags" className={inp} value={tagsInput}
            onChange={(e) => setTagsInput(e.target.value)} placeholder="family, ski, waterfront" />
        </label>

        <label className="md:col-span-2">
          <span className={labelCls}>Description</span>
          <textarea data-testid="admin-destination-field-description" className={inp} rows={5} value={doc.description || ""}
            onChange={(e) => setField("description", e.target.value)} />
        </label>

        {/* Featured image */}
        <div className="md:col-span-2 space-y-2">
          <span className={labelCls}>Featured image</span>
          <div className="flex items-start gap-4">
            <div className="w-40 h-40 rounded-lg overflow-hidden bg-tb-cream-3 flex-shrink-0">
              {doc.featured_image ? (
                <img data-testid="admin-destination-image-preview" src={doc.featured_image} alt="preview" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center font-hand text-tb-navy/40 text-sm">no image</div>
              )}
            </div>
            <div className="flex-1 space-y-2">
              <label className="block">
                <span className="flex items-center gap-1 font-hand text-tb-cream/70 text-xs mb-1"><LinkIcon className="w-3 h-3"/> paste URL</span>
                <input
                  data-testid="admin-destination-field-featured-image"
                  className={inp}
                  value={doc.featured_image || ""}
                  onChange={(e) => setField("featured_image", e.target.value)}
                  placeholder="https://…"
                />
              </label>
              <div>
                <span className="flex items-center gap-1 font-hand text-tb-cream/70 text-xs mb-1"><Upload className="w-3 h-3"/> upload from device</span>
                <input
                  ref={fileRef}
                  data-testid="admin-destination-upload-input"
                  type="file"
                  accept="image/*"
                  onChange={onUpload}
                  disabled={uploading || isNew}
                  className="block text-xs font-hand text-tb-cream"
                />
                {isNew && <p className="text-tb-cream/50 text-xs font-hand mt-1">save the destination first to enable upload</p>}
                {uploading && <p className="text-tb-gold text-xs font-hand mt-1">uploading…</p>}
              </div>
            </div>
          </div>
        </div>

        {/* Read-only booking_metadata */}
        <div className="md:col-span-2 mt-4 p-4 rounded-lg bg-tb-navy border-2 border-tb-gold/30">
          <div className="font-hand text-tb-gold text-sm mb-2">Supplier metadata (read-only)</div>
          {bmKeys.length === 0 ? (
            <div className="font-hand text-tb-cream/50 text-xs italic">no booking metadata yet — Stay22 lookup runs on save</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {bmKeys.map((k) => (
                <label key={k}>
                  <span className={labelCls}>booking_metadata.{k}</span>
                  <input
                    data-testid={`admin-destination-bm-${k}`}
                    className={inpReadOnly}
                    value={typeof bm[k] === "string" ? bm[k] : JSON.stringify(bm[k])}
                    readOnly
                    disabled
                  />
                </label>
              ))}
            </div>
          )}
          <p className="font-hand text-tb-cream/40 text-xs mt-2">
            These fields are set by the Stay22 lookup pipeline and cannot be edited from this form.
          </p>
        </div>

        <div className="md:col-span-2 text-tb-cream/60 font-hand text-xs italic">
          Source: <b>{doc.source || "—"}</b>{doc.updated_at ? ` · Last updated ${doc.updated_at}` : ""}
        </div>
      </div>
    </div>
  );
}
